import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import moment from 'moment';
import { PERIOD_SEPARATOR, SHORT_DATE_FORMAT } from './BillDetails/billDetailsUtils';
import { BILL_STATUSES } from './companyBillRows';
const MONTH_FORMAT = 'YYYY-MM';

/** Workflow stages a bill can sit in, in pipeline order rather than alphabetical. */
export const BILL_STATUS_OPTIONS = [BILL_STATUSES.pending, BILL_STATUSES.processing, BILL_STATUSES.extracted, BILL_STATUSES.validated, BILL_STATUSES.needsProperty,
// Terminal outcomes sit after the pipeline stages and before Error: both are end states a PM may
// want to look at, but neither is a failure of ours.
BILL_STATUSES.rejected, BILL_STATUSES.duplicate, BILL_STATUSES.failed];

/**
 * How recently a bill arrived, as the toolbar offers it. Days rather than named ranges so the
 * comparison is one subtraction and the value survives being read back out of a select.
 */
export const RECEIVED_WITHIN_OPTIONS = [{
  value: '7',
  label: 'Last 7 days'
}, {
  value: '30',
  label: 'Last 30 days'
}, {
  value: '90',
  label: 'Last 90 days'
}];
export const EMPTY_BILL_FILTERS = {
  allocationStates: [],
  apPushEnrolment: '',
  properties: [],
  providers: [],
  receivedWithin: '',
  servicePeriod: '',
  statuses: [],
  utilities: []
};
export function hasActiveBillFilters(filters) {
  var _filters$allocationSt;
  return Boolean(((_filters$allocationSt = filters.allocationStates) === null || _filters$allocationSt === void 0 ? void 0 : _filters$allocationSt.length) || filters.apPushEnrolment || filters.properties.length || filters.providers.length || filters.statuses.length || filters.utilities.length || filters.receivedWithin || filters.servicePeriod);
}
const unique = values => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

/** Options come from the rows themselves, so the lists never offer a value that matches nothing. */
export function getBillFilterOptions(rows) {
  return {
    properties: unique(rows.flatMap(row => row.propertiesLines || [])),
    providers: unique(rows.map(row => row.provider)),
    utilities: unique(rows.flatMap(row => row.utilityTypes || []))
  };
}

/** Rows render the period with the bill details' own format, so the month filter reads it back in that one. */
function coversMonth(servicePeriod, month) {
  const _String$split = String(servicePeriod || '').split(PERIOD_SEPARATOR),
    _String$split2 = _slicedToArray(_String$split, 2),
    startText = _String$split2[0],
    endText = _String$split2[1];
  const start = moment(startText, SHORT_DATE_FORMAT, true);
  const end = moment(endText, SHORT_DATE_FORMAT, true);
  const selected = moment(month, MONTH_FORMAT, true);
  if (!start.isValid() || !end.isValid() || !selected.isValid()) return false;
  return selected.isSameOrAfter(start, 'month') && selected.isSameOrBefore(end, 'month');
}

/** A row has to satisfy every filled-in filter; multiple values within one filter are an OR. */
export function matchesBillFilters(row, filters) {
  if (filters.statuses.length && !filters.statuses.includes(row.status)) return false;
  if (filters.providers.length && !filters.providers.includes(row.provider)) return false;
  if (filters.properties.length && !(row.propertiesLines || []).some(line => filters.properties.includes(line))) {
    return false;
  }
  if (filters.utilities.length && !(row.utilityTypes || []).some(utility => filters.utilities.includes(utility))) {
    return false;
  }

  // A bill with no arrival time on it cannot be shown to be recent, so a recency filter excludes it.
  if (filters.receivedWithin) {
    const receivedAt = row.receivedAt ? moment(row.receivedAt) : null;
    if (!(receivedAt !== null && receivedAt !== void 0 && receivedAt.isValid())) return false;
    if (receivedAt.isBefore(moment().subtract(Number(filters.receivedWithin), 'days'))) return false;
  }
  return !filters.servicePeriod || coversMonth(row.servicePeriod, filters.servicePeriod);
}